/**
 * Утилиты для работы с московским временем (UTC+3)
 * Все даты на фронте должны отображаться в МСК времени
 * На сервере даты хранятся в UTC, но при отображении конвертируются в МСК
 */

const MSK_OFFSET_HOURS = 3; // МСК = UTC + 3 часа

/**
 * Конвертировать UTC дату в московское время
 * @param {Date} utcDate - Дата в UTC
 * @returns {Date} - Дата в МСК (UTC+3)
 */
export const utcToMoscowTime = (utcDate) => {
  if (!utcDate) {
    return null;
  }
  
  // Создаем новую дату с добавлением 3 часов (МСК = UTC + 3)
  const mskDate = new Date(utcDate.getTime() + MSK_OFFSET_HOURS * 60 * 60 * 1000);
  return mskDate;
};

/**
 * Конвертировать московское время в UTC
 * @param {Date} mskDate - Дата в МСК (UTC+3)
 * @returns {Date} - Дата в UTC
 */
export const moscowToUtcTime = (mskDate) => {
  if (!mskDate) {
    return null;
  }
  
  // Создаем новую дату с вычитанием 3 часов (UTC = МСК - 3)
  const utcDate = new Date(mskDate.getTime() - MSK_OFFSET_HOURS * 60 * 60 * 1000);
  return utcDate;
};

/**
 * Получить текущее московское время
 * @returns {Date} - Текущая дата в МСК
 */
export const getMoscowTime = () => {
  const now = new Date();
  return utcToMoscowTime(now);
};

/**
 * Форматировать дату в московском времени
 * @param {Date} date - Дата (может быть в UTC или МСК)
 * @param {string} format - Формат (по умолчанию 'yyyy.MM.dd HH:mm')
 * @returns {string} - Отформатированная строка в МСК времени
 */
export const formatMoscowTime = (date, format = 'yyyy.MM.dd HH:mm') => {
  if (!date) {
    return '';
  }
  
  // Конвертируем в МСК время
  const mskDate = utcToMoscowTime(date);
  
  // Используем UTC методы для форматирования (так как мы уже сдвинули время)
  const year = mskDate.getUTCFullYear();
  const month = String(mskDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(mskDate.getUTCDate()).padStart(2, '0');
  const hours = String(mskDate.getUTCHours()).padStart(2, '0');
  const minutes = String(mskDate.getUTCMinutes()).padStart(2, '0');
  
  return format
    .replace('yyyy', year)
    .replace('MM', month)
    .replace('dd', day)
    .replace('HH', hours)
    .replace('mm', minutes);
};

/**
 * Парсить строку даты как московское время
 * @param {string} dateString - Строка даты в формате yyyy.MM.dd или yyyy.MM.dd HH:mm
 * @returns {Date} - Дата в UTC (для отправки на сервер)
 */
export const parseMoscowTime = (dateString) => {
  if (!dateString) {
    return null;
  }
  
  // Парсим строку как МСК время
  const parts = dateString.trim().split(/[\s\.:]/);
  if (parts.length < 3) {
    return null;
  }
  
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // месяцы 0-11
  const day = parseInt(parts[2], 10);
  const hours = parts.length > 3 ? parseInt(parts[3], 10) : 0;
  const minutes = parts.length > 4 ? parseInt(parts[4], 10) : 0;
  
  // Создаем дату в МСК времени (используем UTC методы, но интерпретируем как МСК)
  const mskDate = new Date(Date.UTC(year, month, day, hours, minutes, 0, 0));
  
  // Конвертируем МСК в UTC для отправки на сервер
  return moscowToUtcTime(mskDate);
};

/**
 * Конвертировать дату из datePicker (локальное время браузера) в МСК время, затем в UTC
 * DatePicker всегда показывает МСК время, независимо от часового пояса браузера
 * @param {Date} localDate - Дата из datePicker (показывает МСК время в локальном формате)
 * @returns {Date} - Дата в UTC (для отправки на сервер)
 */
export const localDatePickerToUtc = (localDate) => {
  if (!localDate) {
    return null;
  }
  
  // datePicker возвращает дату в локальном времени браузера
  // Но мы интерпретируем это как МСК время (независимо от часового пояса браузера)
  // Берем компоненты даты как есть - они уже представляют МСК время
  const year = localDate.getFullYear();
  const month = localDate.getMonth();
  const day = localDate.getDate();
  const hours = localDate.getHours();
  const minutes = localDate.getMinutes();
  
  // Создаем дату как МСК время (используем UTC методы, но интерпретируем компоненты как МСК)
  // Это создает Date объект, где UTC компоненты = МСК компоненты
  const mskDate = new Date(Date.UTC(year, month, day, hours, minutes, 0, 0));
  
  // Конвертируем МСК в UTC для отправки на сервер (вычитаем 3 часа)
  return moscowToUtcTime(mskDate);
};

/**
 * Конвертировать UTC дату в дату для datePicker (показывать как МСК время)
 * DatePicker будет показывать эту дату в локальном времени браузера,
 * но мы интерпретируем это как МСК время
 * @param {Date} utcDate - Дата в UTC
 * @returns {Date} - Дата для отображения в datePicker (МСК компоненты в локальном формате)
 */
export const utcToLocalDatePicker = (utcDate) => {
  if (!utcDate) {
    return null;
  }
  
  // Конвертируем UTC в МСК
  const mskDate = utcToMoscowTime(utcDate);
  
  // Получаем МСК компоненты
  const mskYear = mskDate.getUTCFullYear();
  const mskMonth = mskDate.getUTCMonth();
  const mskDay = mskDate.getUTCDate();
  const mskHours = mskDate.getUTCHours();
  const mskMinutes = mskDate.getUTCMinutes();
  const mskSeconds = mskDate.getUTCSeconds();
  const mskMilliseconds = mskDate.getUTCMilliseconds();
  
  // Создаем Date объект с МСК компонентами
  // Когда браузер отобразит это в локальном времени, мы интерпретируем это как МСК
  // Например: если МСК 15:00, а браузер в UTC-5, то создаем Date который покажет 15:00 локально
  // (хотя это будет другое UTC время, но мы интерпретируем локальное время как МСК)
  return new Date(mskYear, mskMonth, mskDay, mskHours, mskMinutes, mskSeconds, mskMilliseconds);
};
